/**
 * Social Media Error Processor Utility
 * 
 * Processes raw error messages from social media APIs and returns user-friendly
 * messages with actionable instructions for users.
 * 
 * @author Followr Team
 * @version 1.0.0
 */

import { ERROR_PATTERNS, USER_READY_PATTERNS } from './socialErrorPatterns';

/**
 * Social network display names mapping
 */
const SOCIAL_NETWORKS = {
  twitter: 'Twitter',
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  pinterest: 'Pinterest',
  tiktok: 'TikTok',
  threads: 'Threads',
  medium: 'Medium',
  bluesky: 'Bluesky'
};

/**
 * Processes a raw error message and returns a user-friendly error object
 * 
 * @param {string} rawError - The raw error message from the social media API
 * @param {string} socialNetwork - The social network type (twitter, facebook, etc.)
 * @returns {Object} Processed error object with message, action, and severity
 */
export function processSocialError(rawError, socialNetwork) {
  // Input validation
  if (!rawError || typeof rawError !== 'string') {
    return {
      message: 'An unknown error occurred.',
      action: 'Please try again later or contact support.',
      severity: 'warning',
      originalError: rawError
    };
  }

  if (!socialNetwork || typeof socialNetwork !== 'string') {
    socialNetwork = 'social media';
  }

  // Normalize social network name
  const networkName = SOCIAL_NETWORKS[socialNetwork.toLowerCase()] || socialNetwork;

  const userReady = processUserReadyError(rawError, networkName);
  if (userReady) {
    return { ...userReady, originalError: rawError };
  }

  // Clean up the raw error message
  const cleanError = cleanRawError(rawError);

  // Try to match against known error patterns
  const allPatterns = Object.values(ERROR_PATTERNS).flat();
  
  for (const errorPattern of allPatterns) {
    const isMatch = typeof errorPattern.pattern === 'string' 
      ? cleanError.toLowerCase().includes(errorPattern.pattern.toLowerCase())
      : errorPattern.pattern.test(cleanError);

    if (isMatch) {
      return {
        message: errorPattern.message.replace('{network}', networkName),
        action: errorPattern.action.replace('{network}', networkName),
        severity: errorPattern.severity,
        originalError: rawError
      };
    }
  }

  // If no pattern matches, return a generic error
  return {
    message: `There was an issue publishing to ${networkName}.`,
    action: `Please check your ${networkName} connection and try again, or contact support if the problem persists.`,
    severity: 'warning',
    originalError: rawError
  };
}

/**
 * Matches the API's own customer copy (see USER_READY_PATTERNS)
 *
 * @param {string} rawError - The raw error message
 * @param {string} networkName - The display name of the social network
 * @returns {Object|null} Message, action and severity, or null when it is not API copy
 */
function processUserReadyError(rawError, networkName) {
  const text = rawError.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

  for (const errorPattern of USER_READY_PATTERNS) {
    const match = text.match(errorPattern.pattern);
    if (!match) continue;

    if (errorPattern.message) {
      return {
        message: errorPattern.message.replace(/\{network\}/g, networkName).replace(/\{part\}/g, match[1]),
        action: errorPattern.action.replace(/\{network\}/g, networkName),
        severity: errorPattern.severity
      };
    }

    // "What happened. What to do." The first sentence ends at a period that is followed
    // by a space and a capital letter, so hosts like cdn.example.com stay whole.
    const sentences = text.match(/^([\s\S]+?[.!?])\s+(?=[A-Z])([\s\S]+)$/);
    return {
      message: sentences ? sentences[1] : text,
      action: sentences ? sentences[2] : '',
      severity: errorPattern.severity
    };
  }

  return null;
}

/**
 * Cleans and normalizes raw error messages for better pattern matching
 * 
 * @param {string} rawError - The raw error message
 * @returns {string} Cleaned error message
 */
function cleanRawError(rawError) {
  if (!rawError) return '';

  let cleaned = rawError;

  // Remove HTML tags
  cleaned = cleaned.replace(/<[^>]*>/g, '');
  
  // Try to parse JSON errors and extract meaningful content
  try {
    const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      if (parsed.detail) cleaned = parsed.detail;
      else if (parsed.message) cleaned = parsed.message;
      else if (parsed.title) cleaned = parsed.title;
    }
  } catch (e) {
    // Not JSON, continue with original
  }

  // Remove escape characters and normalize whitespace
  cleaned = cleaned
    .replace(/\\n/g, ' ')
    .replace(/\\t/g, ' ')
    .replace(/\\/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  return cleaned;
}

/**
 * Processes multiple errors and returns an array of processed error objects
 * 
 * @param {Array} errors - Array of error objects with rawError and socialNetwork
 * @returns {Array} Array of processed error objects
 */
export function processSocialErrors(errors) {
  if (!Array.isArray(errors)) {
    return [];
  }

  return errors.map(error => {
    const { rawError, socialNetwork, ...rest } = error;
    return {
      ...rest,
      ...processSocialError(rawError, socialNetwork)
    };
  });
}

/**
 * Gets a simplified error message for display in limited space
 * 
 * @param {string} rawError - The raw error message
 * @param {string} socialNetwork - The social network type
 * @returns {string} Simplified error message
 */
export function getSimplifiedErrorMessage(rawError, socialNetwork) {
  const processed = processSocialError(rawError, socialNetwork);
  return processed.message;
}

/**
 * Checks if an error is related to authentication/connection issues
 * 
 * @param {string} rawError - The raw error message
 * @returns {boolean} True if it's an authentication error
 */
export function isAuthenticationError(rawError) {
  if (!rawError) return false;

  const authPatterns = [
    ...ERROR_PATTERNS.connection,
    ...ERROR_PATTERNS.accountStatus
  ];

  const cleanError = cleanRawError(rawError);

  return authPatterns.some(pattern => {
    return typeof pattern.pattern === 'string'
      ? cleanError.toLowerCase().includes(pattern.pattern.toLowerCase())
      : pattern.pattern.test(cleanError);
  });
}

/**
 * Checks if an error is a temporary/retryable error
 * 
 * @param {string} rawError - The raw error message
 * @returns {boolean} True if it's a temporary error
 */
export function isTemporaryError(rawError) {
  if (!rawError) return false;

  const tempPatterns = [
    ...ERROR_PATTERNS.rateLimiting,
    ...ERROR_PATTERNS.serverErrors
  ];

  const cleanError = cleanRawError(rawError);

  return tempPatterns.some(pattern => {
    return typeof pattern.pattern === 'string'
      ? cleanError.toLowerCase().includes(pattern.pattern.toLowerCase())
      : pattern.pattern.test(cleanError);
  });
}

// Export default function for backward compatibility
export default processSocialError; 